import { useMemo, useState } from "react";
import { Check, X, Pencil, AlertTriangle, Undo2, Eraser, RotateCcw } from "lucide-react";
import {
    useNoteCleanupCandidates,
    useNoteCleanupRuns,
    useNoteCleanupApply,
    useNoteCleanupRevert,
    useNoteCleanupUndismiss,
} from "@/api/queries";
import { ErrorState } from "@/components/ErrorState";
import { useToast } from "@/components/Toast";
import { cn, useDraftId, plural, formatDateTime } from "@/lib/utils";
import {
    emptyDecisions, setCluster, setJunk, decidedCount, buildApplyPayload,
    type DecisionState,
} from "@/lib/cleanup-decisions";
import type { NoteCluster, NoteClusterMember, NoteJunkItem } from "@/api/types";

const REASON_TEXT: Record<string, string> = {
    case: "регистр или пробелы",
    translit: "то же слово другой раскладкой",
    levenshtein: "похожее написание",
    no_letters: "нет букв",
    too_short: "слишком короткое",
    too_long: "слишком длинное",
    too_many_words: "это список, а не название",
};

/**
 * Чистка комментариев к тратам (SPEC-055).
 *
 * Экран не решает за владельца: сервер приносит кандидатов с причинами, решение по
 * каждой паре принимается руками, и только подтверждённое уходит в базу. Замер на
 * боевых данных: из 12 авто-склеек порогом 0.85 верны 4 — цена автоматизма здесь
 * выше её пользы.
 */
export function NoteCleanupPage() {
    const [includeDismissed, setIncludeDismissed] = useState(false);
    const [decisions, setDecisions] = useState<DecisionState>(emptyDecisions);
    const candidates = useNoteCleanupCandidates(includeDismissed);
    const runs = useNoteCleanupRuns();
    const apply = useNoteCleanupApply();
    const revert = useNoteCleanupRevert();
    const undismiss = useNoteCleanupUndismiss();
    const toast = useToast();
    // Новый run_id на каждый заход: повтор той же кнопки — продолжение того же
    // прогона, а не второй набор правок поверх первого.
    const runId = useDraftId(apply.isSuccess);

    const data = candidates.data;
    const defaultTargets = useMemo(() => {
        const map: Record<string, string> = {};
        for (const c of data?.clusters ?? []) for (const m of c.members) map[m.note] = c.target;
        return map;
    }, [data?.clusters]);

    // Решения по строкам, ушедшим с экрана (переключили «показать отклонённые»),
    // не в счёт: владелец их больше не видит, а значит и не подтверждает. Считать и
    // применять надо один и тот же набор — иначе кнопка обещает правки, которых не будет.
    const shown = useMemo<DecisionState>(() => {
        // Видимость считаем раздельно: одно и то же значение может быть строкой кластера
        // и строкой мусора, и решение по одной секции не делает видимой другую.
        const inClusters = new Set((data?.clusters ?? []).flatMap((c) => c.members.map((m) => m.note)));
        const inJunk = new Set((data?.junk ?? []).map((j) => j.note));
        return {
            clusters: Object.fromEntries(Object.entries(decisions.clusters).filter(([k]) => inClusters.has(k))),
            junk: Object.fromEntries(Object.entries(decisions.junk).filter(([k]) => inJunk.has(k))),
        };
    }, [data, decisions]);

    const affected = useMemo(() => {
        let n = 0;
        for (const c of data?.clusters ?? []) {
            for (const m of c.members) if (shown.clusters[m.note]?.kind === "accept") n += m.count;
        }
        for (const j of data?.junk ?? []) {
            const v = shown.junk[j.note];
            if (v && (v.kind === "clear" || v.kind === "rewrite")) n += j.count;
        }
        return n;
    }, [data, shown]);

    const decided = decidedCount(shown);

    const onApply = () => {
        const payload = buildApplyPayload(shown, runId, defaultTargets);
        apply.mutate(payload, {
            onSuccess: (r) => {
                // Сбрасываем только отправленное: решения по скрытым строкам владелец
                // принимал осознанно, и терять их при переключении фильтра нельзя.
                setDecisions((s) => ({
                    clusters: Object.fromEntries(Object.entries(s.clusters).filter(([k]) => !(k in shown.clusters))),
                    junk: Object.fromEntries(Object.entries(s.junk).filter(([k]) => !(k in shown.junk))),
                }));
                toast(r.conflicts
                    ? `Применено ${r.applied}, конфликтов ${r.conflicts}`
                    : `Применено ${r.applied} правок`, r.conflicts ? "err" : "ok");
            },
            onError: (e) => toast(e instanceof Error ? e.message : "Не удалось применить", "err"),
        });
    };

    if (candidates.isError) return <ErrorState onRetry={() => candidates.refetch()} />;

    return (
        <div className="space-y-6">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold">Комментарии к тратам</h1>
                    <p className="text-sm text-muted-foreground">
                        {candidates.isLoading
                            ? "Загрузка…"
                            : `Разных значений: ${data?.total_notes ?? 0} · отклонено ранее: ${data?.dismissed ?? 0}`}
                    </p>
                </div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input type="checkbox" checked={includeDismissed}
                        onChange={(e) => setIncludeDismissed(e.target.checked)} />
                    Показать отклонённые ранее
                </label>
            </header>

            {candidates.isLoading && <div className="card p-8 text-center text-muted-foreground">Загрузка…</div>}

            {!candidates.isLoading && !data?.clusters.length && !data?.junk.length && (
                <div className="card p-8 text-center text-muted-foreground">
                    Чистить нечего — комментарии уже единообразны.
                </div>
            )}

            {!!data?.clusters.length && (
                <section className="space-y-3">
                    <h2 className="font-medium">Похожие значения ({data.clusters.reduce((n, c) => n + c.members.length, 0)})</h2>
                    {data.clusters.map((c) => (
                        <ClusterCard key={c.target} cluster={c} decisions={decisions}
                            onSet={(src, v) => setDecisions((s) => setCluster(s, src, v))}
                            onUndismiss={(src, tgt) => undismiss.mutate({ source: src, target: tgt }, {
                                onSuccess: () => toast("Пара вернулась в кандидаты", "ok"),
                                onError: (e) => toast(e instanceof Error ? e.message : "Не удалось вернуть", "err"),
                            })} />
                    ))}
                </section>
            )}

            {!!data?.junk.length && (
                <section className="space-y-3">
                    <h2 className="font-medium">Сомнительные значения ({data.junk.length})</h2>
                    <div className="card divide-y">
                        {data.junk.map((j) => (
                            <JunkRow key={j.note} item={j} decisions={decisions} onSet={(src, v) => setDecisions((s) => setJunk(s, src, v))} />
                        ))}
                    </div>
                </section>
            )}

            {decided > 0 && (
                <div className="sticky bottom-4 flex justify-center">
                    <button className="btn-primary shadow-lg" onClick={onApply} disabled={apply.isPending}>
                        {apply.isPending
                            ? "Применяю…"
                            : `Применить ${decided} ${plural(decided, ["решение", "решения", "решений"])}`
                              + ` · затронет ${affected} ${plural(affected, ["трату", "траты", "трат"])}`}
                    </button>
                </div>
            )}

            {!!runs.data?.runs.length && (
                <section className="space-y-3">
                    <h2 className="font-medium">Прошлые прогоны</h2>
                    <div className="card divide-y text-sm">
                        {runs.data.runs.map((r) => (
                            <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-3">
                                <div>
                                    <div className="num">{r.applied} из {r.planned} {plural(r.planned, ["правки", "правок", "правок"])}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {formatDateTime(r.applied_at ?? r.created_at)}{r.status === "reverted" ? " · откачен" : ""}
                                    </div>
                                </div>
                                {r.status === "applied" && (
                                    <button className="btn-ghost text-sm" disabled={revert.isPending}
                                        onClick={() => revert.mutate(r.id, {
                                            onSuccess: (res) => toast(
                                                res.conflicts
                                                    ? `Откачено ${res.reverted}, пропущено изменённых вручную: ${res.conflicts}`
                                                    : `Откачено ${res.reverted} правок`,
                                                res.conflicts ? "err" : "ok"),
                                            onError: (e) => toast(e instanceof Error ? e.message : "Не удалось откатить", "err"),
                                        })}>
                                        <Undo2 className="h-4 w-4" /> Откатить
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function ClusterCard({ cluster, decisions, onSet, onUndismiss }: {
    cluster: NoteCluster;
    decisions: DecisionState;
    onSet: (source: string, verdict: DecisionState["clusters"][string]) => void;
    onUndismiss: (source: string, target: string) => void;
}) {
    return (
        <div className="card p-4 space-y-3">
            <div className="font-medium">
                {cluster.target} <span className="text-muted-foreground num">· {cluster.target_count}</span>
            </div>
            <div className="space-y-2">
                {cluster.members.map((m) => (
                    <MemberRow key={m.note} member={m} target={cluster.target}
                        verdict={decisions.clusters[m.note]} onSet={(v) => onSet(m.note, v)}
                        onUndismiss={onUndismiss} />
                ))}
            </div>
        </div>
    );
}

function MemberRow({ member, target, verdict, onSet, onUndismiss }: {
    member: NoteClusterMember;
    target: string;
    verdict: DecisionState["clusters"][string] | undefined;
    onSet: (v: DecisionState["clusters"][string]) => void;
    onUndismiss: (source: string, target: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [custom, setCustom] = useState(target);
    const kind = verdict?.kind ?? "pending";

    return (
        <div className={cn("flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
            kind === "accept" ? "bg-primary/10" : kind === "reject" ? "bg-muted opacity-60" : "hover:bg-muted/60")}>
            <span className="font-medium">{member.note}</span>
            <span className="text-muted-foreground num">· {member.count}</span>
            <span className="text-muted-foreground">→</span>
            {editing ? (
                <input autoFocus value={custom} onChange={(e) => setCustom(e.target.value)}
                    onBlur={() => { setEditing(false); if (custom.trim()) onSet({ kind: "accept", target: custom.trim() }); }}
                    className="rounded-md border px-2 py-1 text-sm" />
            ) : (
                <span>{kind === "accept" ? (verdict as { target: string }).target : target}</span>
            )}
            <span className="text-xs text-muted-foreground">
                {member.reasons.map((r) => REASON_TEXT[r] ?? r).join(", ")} · {member.score}
            </span>
            {member.disputed && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600">
                    <AlertTriangle className="h-3 w-3" /> проверить
                </span>
            )}
            {member.dismissed && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">отклонено ранее</span>
            )}
            <div className="ml-auto flex items-center gap-1">
                {member.dismissed && (
                    <button className="btn-ghost text-xs" onClick={() => onUndismiss(member.note, target)}>
                        <RotateCcw className="h-3.5 w-3.5" /> Вернуть
                    </button>
                )}
                <button className="btn-icon" aria-label="Принять" aria-pressed={kind === "accept"}
                    onClick={() => onSet({ kind: "accept", target: custom.trim() || target })}>
                    <Check className={cn("h-4 w-4", kind === "accept" && "text-primary")} />
                </button>
                <button className="btn-icon" aria-label="Отклонить" aria-pressed={kind === "reject"}
                    onClick={() => onSet({ kind: "reject" })}>
                    <X className={cn("h-4 w-4", kind === "reject" && "text-destructive")} />
                </button>
                <button className="btn-icon" aria-label="Исправить канон" onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

function JunkRow({ item, decisions, onSet }: {
    item: NoteJunkItem;
    decisions: DecisionState;
    onSet: (source: string, verdict: DecisionState["junk"][string]) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [custom, setCustom] = useState("");
    const verdict = decisions.junk[item.note];
    const kind = verdict?.kind ?? "pending";

    return (
        <div className={cn("flex flex-wrap items-center gap-2 px-4 py-3 text-sm",
            kind === "clear" ? "bg-destructive/10" : kind === "rewrite" ? "bg-primary/10" : kind === "keep" ? "opacity-60" : "")}>
            <span className="font-medium break-all">«{item.note}»</span>
            <span className="text-muted-foreground num">· {item.count}</span>
            <span className="text-xs text-muted-foreground">{item.reasons.map((r) => REASON_TEXT[r] ?? r).join(", ")}</span>
            {editing && (
                <input autoFocus value={custom} onChange={(e) => setCustom(e.target.value)}
                    onBlur={() => { setEditing(false); if (custom.trim()) onSet(item.note, { kind: "rewrite", target: custom.trim() }); }}
                    placeholder="Новый комментарий"
                    className="rounded-md border px-2 py-1 text-sm" />
            )}
            <div className="ml-auto flex items-center gap-1">
                <button className="btn-ghost text-xs" aria-pressed={kind === "keep"}
                    onClick={() => onSet(item.note, { kind: "keep" })}>Оставить</button>
                <button className="btn-ghost text-xs" aria-pressed={kind === "clear"}
                    onClick={() => onSet(item.note, { kind: "clear" })}>
                    <Eraser className="h-3.5 w-3.5" /> Стереть
                </button>
                <button className="btn-icon" aria-label="Переписать" onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
